import type { MotionmasterRingDraft } from '../functions/motionmaster-ring-draft.function';

/**
 * Shapes returned by the Motionmaster bridge (the Mac mini's FastAPI app, ~/code/motionmaster/server/app.py)
 * as forwarded by onsite-utilities. Field names are the mini's own (snake_case) so the bridge stays a plain
 * pass-through and the mini's web UI and Prism read the same payloads.
 */

/** GET /api/status on the mini: the serial link and the mini's own caches. */
export interface MotionmasterLinkStatus {
  port: string;
  simulated: boolean;
  /** A DNC transfer is in progress right now (directory read, program read or write). */
  busy: boolean;
  last_error: string | null;
  /** ISO timestamp of the last directory read, or null if the mini has never read one. */
  directory_at: string | null;
  /** True when the cached directory came from the control this run (not from the mini's disk copy). */
  directory_live: boolean;
  library_count: number;
  activity: MotionmasterActivityEntry[];
}

export interface MotionmasterActivityEntry {
  at: string;
  kind: string;
  msg: string;
}

/** GET /api/control on the mini: live operating state read from the control's variables. */
export interface MotionmasterControlState {
  /** Fagor OPMODE: 0 main menu, 10 automatic execution, 11 single block, 30 editing, 70 DNC, ... */
  mode: number;
  mode_text: string;
  /** Program selected for execution, or null when none is. */
  program: number | null;
  block: number | null;
  pos: {
    X: number | null;
    Y: number | null;
    Z: number | null;
    U: number | null;
    V: number | null;
  };
  feed: number | null;
  override: number | null;
  at: string;
}

/** onsite-utilities GET motionmaster/status: both of the above in one round trip. */
export interface MotionmasterStatusResponse {
  status: MotionmasterLinkStatus;
  /** Null when the mini answered but the control did not (link busy, control off, DNC error). */
  control: MotionmasterControlState | null;
  controlError: string | null;
}

/** One line of the control's program directory. */
export interface MotionmasterDirectoryEntry {
  /** 'P000130', or a non-program entry such as 'PLC_PRG'. */
  program: string;
  /** Numeric program number, null for non-program entries. */
  number: number | null;
  comment: string;
  size: number | null;
  /** Control's own stamp, dd/mm/yy and hh:mm:ss. */
  date: string;
  time: string;
  attr: string;
  in_library: boolean;
}

export interface MotionmasterDirectoryResponse {
  entries: MotionmasterDirectoryEntry[];
  at: string | null;
  live: boolean;
}

/** GET /api/programs/{n} on the mini: the program as it is on the control right now. */
export interface MotionmasterProgramResponse {
  number: number;
  text: string;
  source: 'control' | 'library';
  library_text: string | null;
  same: boolean;
  comment: string;
}

export interface MotionmasterSelectProgramRequest {
  programNumber: number;
}

/** A Cycle Start request must name the program the operator expects to be selected. */
export interface MotionmasterStartProgramRequest {
  programNumber: number;
  expectedDigest: string;
}

/** Cycle Stop is scoped to the program the operator has open in Prism. */
export interface MotionmasterStopProgramRequest {
  programNumber: number;
}

/** Control stamp as the program versions store it: 'dd/mm/yy hh:mm:ss'. */
export function motionmasterControlStamp(
  entry: Pick<MotionmasterDirectoryEntry, 'date' | 'time'>
): string {
  return `${entry.date} ${entry.time}`;
}

// ---- toolpath (GET /api/programs/{n}/toolpath on the mini, fagor_dnc/toolpath.py) ----------------------

export type MotionmasterXyz = [number, number, number];

export interface MotionmasterToolpathBounds {
  min: MotionmasterXyz;
  max: MotionmasterXyz;
}

export interface MotionmasterToolpathSegment {
  /** 1-based line of the program block that produced this move. */
  line: number;
  type: 'rapid' | 'feed' | 'arc';
  /** Programmed (head pivot) positions; two points for a straight move, many for an arc. */
  points: MotionmasterXyz[];
  u: number;
  v: number;
  feed: number | null;
  length: number;
  /** The first positioning move: its start is wherever the head was, so there is nothing to draw. */
  from_origin?: boolean;
  /** Tool-tip positions with the U/V kinematics applied; absent when the mini could not compute them. */
  tip?: MotionmasterXyz[];
}

export interface MotionmasterToolpathMarker {
  line: number;
  kind: 'hole' | 'call' | 'dwell' | 'm';
  x: number;
  y: number;
  z: number;
  label: string;
  diameter?: number | null;
  /** True when the hole macro's actual centerline has been expanded into segments. */
  expanded?: boolean;
}

export interface MotionmasterToolpathStats {
  blocks: number;
  rapid_length: number;
  feed_length: number;
  est_minutes: number;
  holes: number;
  end: MotionmasterXyz;
}

export interface MotionmasterToolpath {
  segments: MotionmasterToolpathSegment[];
  markers: MotionmasterToolpathMarker[];
  unparsed: { line: number; text: string; why: string }[];
  bounds: MotionmasterToolpathBounds | null;
  cut_bounds: MotionmasterToolpathBounds | null;
  tip_bounds: MotionmasterToolpathBounds | null;
  stickout: number;
  /** Optional, verified local ring study. Absent on ordinary machine programs. */
  ringDraft?: MotionmasterRingDraft;
  stats: MotionmasterToolpathStats;
}
